import React, { useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  HiOutlineRefresh, 
  HiOutlineVideoCamera, 
  HiOutlinePhone, 
  HiOutlineOfficeBuilding, 
  HiOutlineDesktopComputer,
  HiOutlineExternalLink,
  HiOutlineLocationMarker,
  HiOutlineClock,
  HiOutlineUsers,
  HiOutlineDocumentText,
  HiOutlinePlus
} from 'react-icons/hi';
import { toast } from 'react-hot-toast';
import calendarService, { CalendarEvent } from '../services/calendarService';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEscapeModal, hasFormChanges } from '../utils/useEscapeModal';

interface InterviewEvent extends CalendarEvent {
  interviewType?: 'phone' | 'video' | 'onsite' | 'technical' | 'behavioral' | 'final' | 'other';
  companyName?: string;
  position?: string;
  meetingLink?: string;
  interviewerName?: string;
  notes?: string;
}

interface EventDetailsModalProps {
  event: InterviewEvent | null;
  isOpen: boolean;
  onClose: () => void;
}

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: {
    summary: string;
    description?: string;
    start: { dateTime: string; timeZone: string };
    end: { dateTime: string; timeZone: string };
    location?: string;
  }) => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    startDateTime: '',
    endDateTime: '',
    location: ''
  });

  // Initial form data for change detection
  const [initialFormData, setInitialFormData] = useState({
    summary: '',
    description: '',
    startDateTime: '',
    endDateTime: '',
    location: ''
  });

  // Reset form data
  const resetForm = () => {
    const defaultFormData = {
      summary: '',
      description: '',
      startDateTime: '',
      endDateTime: '',
      location: ''
    };
    setFormData(defaultFormData);
    setInitialFormData(defaultFormData);
    onClose();
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = hasFormChanges(formData, initialFormData);

  // Handle ESC key to close modal with confirmation for unsaved changes
  useEscapeModal({
    isOpen,
    onClose: resetForm,
    hasUnsavedChanges,
    confirmMessage: 'You have unsaved changes. Are you sure you want to close without saving?'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.summary || !formData.startDateTime || !formData.endDateTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    const startTime = new Date(formData.startDateTime);
    const endTime = new Date(formData.endDateTime);
    
    if (startTime >= endTime) {
      toast.error('Start time must be before end time');
      return;
    }

    const eventData = {
      summary: formData.summary,
      description: formData.description,
      start: {
        dateTime: formData.startDateTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: formData.endDateTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      location: formData.location
    };

    onSubmit(eventData);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Create New Event</h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.summary}
                onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Technical Interview - Google"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time *
              </label>
              <input
                type="datetime-local"
                value={formData.startDateTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startDateTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time *
              </label>
              <input
                type="datetime-local"
                value={formData.endDateTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endDateTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 123 Main St or https://zoom.us/j/123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Interview details, preparation notes, etc."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create Event
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({ event, isOpen, onClose }) => {
  // Handle ESC key to close modal (no form changes to track)
  useEscapeModal({
    isOpen,
    onClose,
    hasUnsavedChanges: false
  });

  if (!isOpen || !event) return null;

  const getInterviewTypeIcon = (type?: string) => {
    switch (type) {
      case 'phone': return <HiOutlinePhone className="h-5 w-5 text-green-600" />;
      case 'video': return <HiOutlineVideoCamera className="h-5 w-5 text-blue-600" />;
      case 'onsite': return <HiOutlineOfficeBuilding className="h-5 w-5 text-purple-600" />;
      case 'technical': return <HiOutlineDesktopComputer className="
      h-5 w-5 text-orange-600" />;
      default: return <HiOutlineUsers className="h-5 w-5 text-gray-600" />;
    }
  };

  const getInterviewTypeColor = (type?: string) => {
    switch (type) {
      case 'phone': return 'bg-green-100 text-green-800 border-green-200';
      case 'video': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'onsite': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'technical': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'behavioral': return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'final': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const startTime = new Date(event.start.dateTime);
  const endTime = new Date(event.end.dateTime);
  const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));

  // Extract interview details from summary/description
  const interviewType = event.summary.toLowerCase().includes('phone') ? 'phone' :
                       event.summary.toLowerCase().includes('video') ? 'video' :
                       event.summary.toLowerCase().includes('onsite') ? 'onsite' :
                       event.summary.toLowerCase().includes('technical') ? 'technical' :
                       event.summary.toLowerCase().includes('behavioral') ? 'behavioral' :
                       event.summary.toLowerCase().includes('final') ? 'final' : 'other';

  // Extract company name from summary (assuming format like "Interview - CompanyName")
  const companyMatch = event.summary.match(/(?:Interview|interview).*?[-–]\s*(.+?)(?:\s|$)/);
  const companyName = companyMatch ? companyMatch[1].trim() : 'Unknown Company';

  // Extract meeting link from description
  const meetingLinkMatch = event.description?.match(/(https?:\/\/[^\s]+(?:zoom|meet|teams|webex)[^\s]*)/i);
  const meetingLink = meetingLinkMatch ? meetingLinkMatch[1] : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {getInterviewTypeIcon(interviewType)}
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getInterviewTypeColor(interviewType)}`}>
                  {interviewType.charAt(0).toUpperCase() + interviewType.slice(1)} Interview
                </span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {event.summary}
              </h2>
              <p className="text-lg text-gray-700 font-medium">{companyName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Time & Duration */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <HiOutlineClock className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-900">Schedule</span>
            </div>
            <div className="text-sm text-gray-700">
              <div>{startTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</div>
              <div className="font-mono">
                {startTime.toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit', 
                  hour12: true 
                })} - {endTime.toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit', 
                  hour12: true 
                })}
              </div>
              <div className="text-gray-500">Duration: {duration} minutes</div>
            </div>
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-3 mb-4">
              <HiOutlineLocationMarker className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <span className="font-medium text-gray-900">Location</span>
                <p className="text-gray-700 text-sm">{event.location}</p>
              </div>
            </div>
          )}

          {/* Meeting Link */}
          {meetingLink && (
            <div className="mb-4">
              <a
                href={meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <HiOutlineVideoCamera className="h-5 w-5" />
                Join Meeting
                <HiOutlineExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}

          {/* Attendees */}
          {event.attendees && event.attendees.length > 0 && (
            <div className="flex items-start gap-3 mb-4">
              <HiOutlineUsers className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <span className="font-medium text-gray-900">Attendees</span>
                <div className="space-y-1">
                  {event.attendees.map((attendee, index) => (
                    <div key={index} className="text-sm text-gray-700">
                      {attendee.displayName || attendee.email}
                      {attendee.responseStatus && (
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                          attendee.responseStatus === 'accepted' ? 'bg-green-100 text-green-700' :
                          attendee.responseStatus === 'declined' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {attendee.responseStatus}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Description/Notes */}
          {event.description && (
            <div className="flex items-start gap-3 mb-4">
              <HiOutlineDocumentText className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <span className="font-medium text-gray-900">Notes</span>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{event.description}</p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            {/* Primary Actions */}
            <div className="flex gap-3">
              {/* Join Meeting Button - Show if it's a video interview with meeting link */}
              {meetingLink && interviewType === 'video' && (
                <a
                  href={meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <HiOutlineVideoCamera className="h-4 w-4" />
                  Join Meeting
                </a>
              )}
              
              {/* Call Button - Show if it's a phone interview */}
              {interviewType === 'phone' && event.location && (
                <a
                  href={`tel:${event.location.replace(/\D/g, '')}`}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <HiOutlinePhone className="h-4 w-4" />
                  Call Now
                </a>
              )}
              
              {/* Navigation Button - Show if it's an onsite interview */}
              {interviewType === 'onsite' && event.location && (
                <a
                  href={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                >
                  <HiOutlineLocationMarker className="h-4 w-4" />
                  Get Directions
                </a>
              )}
            </div>

            {/* Secondary Actions */}
            <div className="flex gap-3">
              <a
                href={event.htmlLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <HiOutlineExternalLink className="h-4 w-4" />
                Open in Google Calendar
              </a>
              
              <button
                onClick={() => {
                  // Copy event details to clipboard
                  const eventText = `${event.summary}\n${startTime.toLocaleDateString()} ${startTime.toLocaleTimeString()}\n${event.location || 'Location TBD'}\n${meetingLink || ''}`;
                  navigator.clipboard.writeText(eventText);
                  toast.success('Event details copied to clipboard');
                }}
                className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <HiOutlineDocumentText className="h-4 w-4" />
                Copy Details
              </button>
            </div>

            {/* Preparation Quick Actions */}
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Interview Preparation</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    window.open(`https://www.google.com/search?q=${encodeURIComponent(companyName + ' interview questions')}`, '_blank');
                  }}
                  className="text-xs px-3 py-1 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  Research Questions
                </button>
                <button
                  onClick={() => {
                    window.open(`https://www.glassdoor.com/Search/results.htm?keyword=${encodeURIComponent(companyName)}`, '_blank');
                  }}
                  className="text-xs px-3 py-1 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  Company Reviews
                </button>
                <button
                  onClick={() => {
                    window.open(`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(companyName)}`, '_blank');
                  }}
                  className="text-xs px-3 py-1 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  LinkedIn
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Calendar: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<InterviewEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventFilter, setEventFilter] = useState<string>('all');
  const calendarRef = useRef<FullCalendar>(null);
  const queryClient = useQueryClient();

  // Check calendar access
  const { data: accessData, isLoading: accessLoading } = useQuery(
    'calendar-access',
    calendarService.checkAccess,
    {
      retry: false,
      onError: (error: any) => {
        console.error('Calendar access error:', error);
        if (error.response?.status === 403) {
          toast.error('Calendar access not available. Please re-authenticate with Google.');
        }
      }
    }
  );

  // Get all events
  const { data: events, isLoading: eventsLoading, refetch: refetchEvents } = useQuery(
    'calendar-events',
    () => calendarService.getEvents(),
    {
      enabled: !!accessData?.hasAccess,
      retry: false,
      onError: (error: any) => {
        console.error('Calendar events error:', error);
        toast.error('Failed to load calendar events');
      }
    }
  );

  // Transform events for FullCalendar
  const calendarEvents = events?.map(event => {
    const interviewType = event.summary.toLowerCase().includes('phone') ? 'phone' :
                         event.summary.toLowerCase().includes('video') ? 'video' :
                         event.summary.toLowerCase().includes('onsite') ? 'onsite' :
                         event.summary.toLowerCase().includes('technical') ? 'technical' :
                         event.summary.toLowerCase().includes('behavioral') ? 'behavioral' :
                         event.summary.toLowerCase().includes('final') ? 'final' : 'other';

    const isInterview = event.summary.toLowerCase().includes('interview');

    return {
      id: event.id,
      title: event.summary,
      start: event.start.dateTime,
      end: event.end.dateTime,
      extendedProps: {
        ...event,
        interviewType,
        isInterview
      },
      backgroundColor: isInterview ? (
        interviewType === 'phone' ? '#10b981' :
        interviewType === 'video' ? '#3b82f6' :
        interviewType === 'onsite' ? '#8b5cf6' :
        interviewType === 'technical' ? '#f59e0b' :
        interviewType === 'behavioral' ? '#ec4899' :
        interviewType === 'final' ? '#ef4444' : '#6b7280'
      ) : '#94a3b8',
      borderColor: isInterview ? (
        interviewType === 'phone' ? '#059669' :
        interviewType === 'video' ? '#2563eb' :
        interviewType === 'onsite' ? '#7c3aed' :
        interviewType === 'technical' ? '#d97706' :
        interviewType === 'behavioral' ? '#db2777' :
        interviewType === 'final' ? '#dc2626' : '#4b5563'
      ) : '#64748b',
      textColor: '#ffffff'
    };
  }).filter(event => {
    if (eventFilter === 'all') return true;
    if (eventFilter === 'interviews') return event.extendedProps.isInterview;
    return event.extendedProps.interviewType === eventFilter;
  }) || [];

  const handleEventClick = (clickInfo: any) => {
    const event = clickInfo.event.extendedProps as InterviewEvent;
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Create event mutation
  const createEventMutation = useMutation(
    (eventData: any) => calendarService.createEvent(eventData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('calendar-events');
        toast.success('Event created successfully');
      },
      onError: (error: any) => {
        console.error('Create event error:', error);
        toast.error('Failed to create event');
      }
    }
  );

  const handleCreateEvent = (eventData: any) => {
    createEventMutation.mutate(eventData);
  };

  const handleRefresh = () => {
    refetchEvents();
    toast.success('Calendar refreshed');
  };

  if (accessLoading) {
    return <LoadingSpinner />;
  }

  if (!accessData?.hasAccess) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <HiOutlineClock className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            Calendar Access Required
          </h2>
          <p className="text-yellow-700 mb-4">
            To view your interview schedule, you need to grant calendar permissions to the application.
          </p>
          <a
            href="/auth/google"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Re-authenticate with Google
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interview Calendar</h1>
          <p className="text-gray-600">View and manage your scheduled interviews</p>
        </div>
        
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          {/* Filter Dropdown */}
          <div className="relative">
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="flex items-center px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Events</option>
              <option value="interviews">All Interviews</option>
              <option value="phone">Phone Interviews</option>
              <option value="video">Video Interviews</option>
              <option value="onsite">On-site Interviews</option>
              <option value="technical">Technical Interviews</option>
              <option value="behavioral">Behavioral Interviews</option>
              <option value="final">Final Interviews</option>
            </select>
          </div>

          <button
            onClick={handleRefresh}
            disabled={eventsLoading}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <HiOutlineRefresh className={`h-4 w-4 mr-2 ${eventsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <HiOutlinePlus className="h-5 w-5 mr-2" />
            New Event
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Interview Types</h3>
        <div className="flex flex-wrap gap-4 text-xs">
          {[
            { type: 'phone', color: 'bg-green-500', label: 'Phone' },
            { type: 'video', color: 'bg-blue-500', label: 'Video' },
            { type: 'onsite', color: 'bg-purple-500', label: 'On-site' },
            { type: 'technical', color: 'bg-orange-500', label: 'Technical' },
            { type: 'behavioral', color: 'bg-pink-500', label: 'Behavioral' },
            { type: 'final', color: 'bg-red-500', label: 'Final' },
            { type: 'other', color: 'bg-gray-500', label: 'Other Events' }
          ].map(({ type, color, label }) => (
            <div key={type} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${color}`}></div>
              <span className="text-gray-700">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {eventsLoading ? (
          <div className="p-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="m-2 mt-4">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
            }}
            events={calendarEvents}
            eventClick={handleEventClick}
            height="auto"
            eventDisplay="block"
            dayMaxEvents={3}
            moreLinkClick="popover"
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            slotLabelFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short'
            }}
            nowIndicator={true}
            selectable={true}
            selectMirror={true}
            weekends={true}
            eventMouseEnter={(mouseEnterInfo: any) => {
              mouseEnterInfo.el.style.cursor = 'pointer';
            }}
            eventClassNames="transition-all duration-200 hover:opacity-80"
          />
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      <EventDetailsModal
        event={selectedEvent}
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
        }}
      />

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateEvent}
      />
    </div>
  );
};

export default Calendar; 